import type { HostConfig } from '../host'

/**
 * The unbranded baseline — and deliberately NOT in `HOSTS`.
 *
 * Two jobs, both of them "what does this app look like with no tenant":
 *
 * 1. It is what `index.html` ships as its literal copy, so the bundle in git
 *    carries no tenant's name. Every host's real name arrives by rewrite.
 * 2. It is the client's fallback for a hostname that resolves to nothing.
 *
 * Keeping it out of `HOSTS` is the important part. The worker 404s an
 * unrecognised hostname on purpose (falling back would proxy an unknown
 * caller's requests into some host's API), and putting this in the table would
 * hand that unknown caller a working app pointed at `webOrigin` below. The
 * client's fallback is a different risk entirely — worst case is unbranded
 * copy and a dead login link — which is why the asymmetry is deliberate rather
 * than an oversight.
 *
 * It follows that `webOrigin` here should never successfully serve an API.
 */
export const genericHost: HostConfig = {
  key: 'generic',
  hostnames: [],
  // Reserved by RFC 2606 and guaranteed not to resolve. A fallback that
  // silently talked to a real origin would be worse than one that fails.
  webOrigin: 'https://studio.invalid',
  loginPath: '/auth/login',

  brand: {
    productName: 'Studio',
    orgName: 'your workspace',
    tagline: 'Record, dictate or write — then publish.',
    description: 'Record clips on-device, then publish them to your workspace.',
    // A plain ring: a lens, an aperture, a record button — legible at 24px and
    // owned by nobody. Bounds are 0–110 to match the shell's -14 -14 138 138
    // viewBox, the same box every host's mark is drawn in.
    markPath:
      'M0,55 A55,55 0 1,0 110,55 A55,55 0 1,0 0,55 Z M22,55 A33,33 0 1,0 88,55 A33,33 0 1,0 22,55 Z',
    faviconPath: '/brand/generic/favicon.svg',
    themeColorDark: '#141414',
    themeColorLight: '#FFFFFF',
    // No palette: global.css's own neutral slate IS this host's palette, and
    // restating it here would give the same colours two homes to drift between.
  },

  capabilities: {
    transcription: false,
    metadataGeneration: false,
    notesAssistant: false,
  },

  destination: {
    level1: { label: 'Workspace', plural: 'Workspaces' },
    level2: { label: 'Destination', plural: 'Destinations' },
  },

  storage: {
    databaseName: 'studio',
    keyPrefix: 'studio.',
    backupExtension: '.studiovideo',
  },
}
